// src/components/operationCenter/widgets/NewsFeedWidget.tsx
// Widget wrapper for News Feed

import React, { useState, useCallback, useMemo, useEffect } from "react";
import type { WidgetProps, NewsFeedConfig } from "@/types/dashboard";
import { useFetchNews } from "@/hooks";
import { DynamicIcon } from "lucide-react/dynamic";
import CompactTokenSelector from "./shared/CompactTokenSelector";
import { useTokensScreenerData } from "@/hooks/tokens/useTokensScreenerData";

// Format relative time helper
const formatRelativeTime = (isoString: string): string => {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays > 0) return `${diffDays}d`;
  if (diffHours > 0) return `${diffHours}h`;
  const diffMins = Math.floor(diffMs / (1000 * 60));
  return `${diffMins}m`;
};

// Generate gradient based on source name
const getAvatarGradient = (source: string | null): string => {
  if (!source) return "from-blue-500 to-purple-600";

  let hash = 0;
  for (let i = 0; i < source.length; i++) {
    hash = source.charCodeAt(i) + ((hash << 5) - hash);
  }

  const gradients = [
    "from-blue-500 to-purple-600",
    "from-emerald-500 to-teal-600",
    "from-orange-500 to-red-600",
    "from-pink-500 to-rose-600",
    "from-indigo-500 to-blue-600",
    "from-cyan-500 to-blue-600",
    "from-amber-500 to-orange-600",
    "from-violet-500 to-purple-600",
  ];

  return (
    gradients[Math.abs(hash) % gradients.length] ??
    "from-blue-500 to-purple-600"
  );
};

// Get initials from source name
const getInitials = (source: string | null): string => {
  if (!source) return "?";
  const words = source.trim().split(/\s+/);
  if (words.length === 1) {
    return words[0]?.substring(0, 2).toUpperCase() ?? "?";
  }
  return (
    (words[0]?.[0] ?? "").toUpperCase() + (words[1]?.[0] ?? "").toUpperCase()
  );
};

interface NewsItemProps {
  news: {
    url: string;
    title: string | null;
    content: string | null;
    source: string;
    author_name: string | null;
    profile_image: string | null;
    published_at: string | null;
    created_at: string | null;
    ticker: string | null;
  };
}

const NewsItem: React.FC<NewsItemProps> = ({ news }) => {
  const gradient = getAvatarGradient(news.source);
  const initials = getInitials(news.source);
  const displayTime = news.published_at || news.created_at;

  return (
    <a
      href={news.url}
      target="_blank"
      rel="noopener noreferrer"
      className="block p-3 hover:bg-gray-700/30 rounded-lg transition-colors group"
    >
      <div className="flex gap-3">
        {/* Avatar */}
        <div className="flex-shrink-0">
          {news.profile_image ? (
            <img
              src={news.profile_image}
              alt={news.author_name || news.source}
              className="h-8 w-8 rounded-full bg-gray-700 object-cover"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = "none";
                const fallback =
                  target.nextElementSibling as HTMLElement | null;
                if (fallback) fallback.style.display = "flex";
              }}
            />
          ) : null}
          <div
            className={`h-8 w-8 rounded-full bg-gradient-to-br ${gradient} flex items-center justify-center font-bold text-xs shadow-md text-white`}
            style={{ display: news.profile_image ? "none" : "flex" }}
          >
            {initials}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-medium text-gray-200 truncate">
              {news.author_name || news.source}
            </span>
            <span className="text-xs text-gray-500">•</span>
            <span className="text-xs text-gray-500">
              {displayTime ? formatRelativeTime(displayTime) : ""}
            </span>
          </div>

          <p className="text-sm text-gray-300 line-clamp-2 group-hover:text-white transition-colors">
            {news.title || news.content}
          </p>

          {news.ticker && (
            <div className="mt-1.5 flex items-center gap-1">
              <span className="px-1.5 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded-md font-medium">
                ${news.ticker}
              </span>
            </div>
          )}
        </div>
      </div>
    </a>
  );
};

const NewsFeedWidget: React.FC<WidgetProps<NewsFeedConfig>> = ({ widget, onConfigChange }) => {
  const config = widget.config as NewsFeedConfig;
  const limit = config?.limit ?? 20;

  // State - initialized from config
  const [selectedTickers, setSelectedTickers] = useState<string[]>(
    config?.tickers ?? []
  );

  // Sync state with config when config changes externally
  useEffect(() => {
    const configTickers = config?.tickers ?? [];
    setSelectedTickers(configTickers);
  }, [config?.tickers]);

  // Get available tokens from screener
  const { data: rows } = useTokensScreenerData();
  const availableTokens = useMemo(() => {
    if (!Array.isArray(rows)) return [];
    return rows
      .map(r => String(r["tokenSymbol"] ?? "").toUpperCase())
      .filter(Boolean)
      .slice(0, 100);
  }, [rows]);

  const { news, isLoading, isError } = useFetchNews({ limit });

  // Handle ticker change
  const handleTickerChange = useCallback((value: string | string[]) => {
    const tickers = Array.isArray(value) ? value : [value];
    setSelectedTickers(tickers);
    onConfigChange?.({ tickers });
  }, [onConfigChange]);

  // Filter news by tickers if configured
  const filteredNews = useMemo(() => {
    if (selectedTickers.length === 0) {
      return news;
    }
    const tickerSet = new Set(selectedTickers.map((t) => t.toUpperCase().trim()));
    return news.filter((item) => {
      // Must have a valid non-empty ticker to be included in filtered results
      const ticker = item.ticker?.trim();
      if (!ticker) return false;
      return tickerSet.has(ticker.toUpperCase());
    });
  }, [news, selectedTickers]);

  // Header bar component
  const HeaderBar = (
    <div className="flex-shrink-0 flex items-center gap-2 px-3 py-2 border-b border-gray-700/30">
      <span className="text-xs uppercase tracking-wide text-slate-300">
        {widget.title}
      </span>
      <div className="ml-auto flex items-center gap-2">
        <CompactTokenSelector
          value={selectedTickers}
          onChange={handleTickerChange}
          multiple
          {...(availableTokens.length > 0 ? { tokens: availableTokens } : {})}
          placeholder="All tokens"
        />
        {selectedTickers.length > 0 && (
          <span className="text-[10px] text-slate-500">
            {selectedTickers.length} filter{selectedTickers.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="h-full flex flex-col overflow-hidden">
        {HeaderBar}
        <div className="flex-1 flex items-center justify-center">
          <div className="flex items-center gap-2 text-gray-400">
            <DynamicIcon name="loader-2" className="w-5 h-5 animate-spin" />
            <span className="text-sm">Loading news...</span>
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="h-full flex flex-col overflow-hidden">
        {HeaderBar}
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center text-gray-400">
            <DynamicIcon
              name="alert-circle"
              className="w-8 h-8 mx-auto mb-2 text-red-400"
            />
            <p className="text-sm">Failed to load news</p>
          </div>
        </div>
      </div>
    );
  }

  if (filteredNews.length === 0) {
    return (
      <div className="h-full flex flex-col overflow-hidden">
        {HeaderBar}
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center text-gray-400">
            <DynamicIcon name="newspaper" className="w-8 h-8 mx-auto mb-2" />
            <p className="text-sm">No news available</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {HeaderBar}
      <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar">
        <div className="space-y-1 p-2">
          {filteredNews.map((item, idx) => (
            <NewsItem key={`${item.url}-${idx}`} news={item} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default NewsFeedWidget;
